import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineBell,
  HiOutlineSearch,
  HiOutlineCalendar,
  HiOutlineDocumentText,
  HiOutlineEye,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineFilter,
  HiOutlineSparkles,
  HiOutlineOfficeBuilding,
  HiOutlineX,
  HiOutlineRefresh
} from "react-icons/hi";
import Cookies from "js-cookie";
import { useSearch } from "../../Context/SearchContext";

const Notice = () => {
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedNotice, setSelectedNotice] = useState(null);
  const { selectedEvent } = useSearch();
  
  // Refs
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);

  // ✅ UX FIX 1: Debounce search for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ✅ UX FIX 2: Escape key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setSelectedNotice(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setLoading(true);
        const token = Cookies.get("userToken");
        if (!token) {
          setError("User not authenticated");
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/user/getAllNotice`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (!response.ok) {
          setError(data?.message || "Failed to fetch notices");
          return;
        }

        setNotices(Array.isArray(data?.data) ? data.data : []);
      } catch {
        setError("Something went wrong while fetching notices.");
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedEvent]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const filteredData = notices.filter((notice) => {
    const searchLower = debouncedSearchTerm.toLowerCase();
    const selectedEventTitle = selectedEvent?.title?.toLowerCase() || "";
    const collegeName = notice.adminId?.collegeName || "";

    const matchesSelectedEvent = selectedEventTitle
      ? notice.title.toLowerCase().includes(selectedEventTitle)
      : true;

    const matchesSearch = searchLower
      ? (
          notice.title.toLowerCase().includes(searchLower) ||
          notice.description.toLowerCase().includes(searchLower) ||
          collegeName.toLowerCase().includes(searchLower)
        )
      : true;

    return matchesSelectedEvent && matchesSearch;
  });

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    searchInputRef.current?.focus();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, scale: 1.2 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 1.2, ease: "easeOut" }
    }
  };

  const tableRowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.05, duration: 0.3 }
    }),
    hover: {
      scale: 1.01,
      backgroundColor: "rgba(156, 163, 175, 0.1)",
      transition: { duration: 0.2 }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 50 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { type: "spring", damping: 25, stiffness: 300 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8, 
      y: 50,
      transition: { duration: 0.2 }
    }
  };

  // Skeleton Loader
  const SkeletonRow = () => (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-5 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-8"></div>
      <div className="flex items-center gap-1">
        <div className="w-4 h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>
      <div className="h-4 bg-gray-200 rounded w-32"></div>
      <div className="h-4 bg-gray-200 rounded w-28"></div>
      <div className="h-4 bg-gray-200 rounded w-40"></div>
    </div>
  );

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200"
    >
      <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <HiOutlineDocumentText className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">
        {debouncedSearchTerm ? 'No notices found' : 'No notices yet'}
      </h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        {debouncedSearchTerm 
          ? `No notices matching "${debouncedSearchTerm}"`
          : 'There are no notices available at the moment.'}
      </p>
      {debouncedSearchTerm && (
        <button
          onClick={clearSearch}
          className="px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition inline-flex items-center gap-2"
        >
          <HiOutlineX className="w-5 h-5" />
          Clear Search
        </button>
      )}
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden"
    >
      {/* Background blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{ x: [0, 50, 0], y: [0, -30, 0] }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{ x: [0, -50, 0], y: [0, 30, 0] }}
          transition={{ duration: 18, repeat: Infinity, repeatType: "reverse" }}
        />
      </div>

      {/* Hero Section */}
      <div className="relative w-full h-[40vh] md:h-[50vh] overflow-hidden">
        <motion.div
          variants={heroVariants}
          initial="hidden"
          animate="visible"
          className="absolute inset-0 bg-gradient-to-r from-gray-100 via-white to-gray-100"
        />

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="w-20 h-20 bg-gray-200 rounded-2xl flex items-center justify-center mb-6 shadow-lg border border-gray-300"
          >
            <HiOutlineBell className="w-10 h-10 text-gray-600" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold text-gray-700 mb-4"
          >
            📢 Notice Board
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-base md:text-lg text-gray-500 max-w-2xl"
          >
            Stay informed with the latest announcements and updates from our alumni network
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex items-center space-x-4 mt-8 bg-white/60 backdrop-blur-md rounded-full px-6 py-3 border border-gray-200"
          >
            <div className="flex items-center space-x-2">
              <HiOutlineDocumentText className="w-5 h-5 text-gray-600" />
              <span className="text-gray-600 text-sm font-medium">
                {filteredData.length} Notice{filteredData.length !== 1 ? 's' : ''}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12"
      >
        {/* Controls */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8 bg-white/80 backdrop-blur-sm rounded-xl p-4 md:p-6 shadow-sm border border-gray-200"
        >
          <div className="flex items-center gap-3 text-gray-600 w-full md:w-auto">
            <HiOutlineFilter className="w-5 h-5" />
            <span className="text-sm">Show</span>
            <select
              className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white text-gray-700 text-sm"
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
            </select>
            <span className="text-sm">entries</span>
          </div>

          <div className="relative w-full md:w-80">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              ref={searchInputRef}
              type="text"
              className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 bg-white text-gray-700 placeholder-gray-400 text-sm"
              placeholder="Search notices..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <HiOutlineX className="w-5 h-5" />
              </button>
            )}
          </div>
        </motion.div>

        {/* Results count */}
        {!loading && filteredData.length > 0 && (
          <motion.div variants={itemVariants} className="mb-4 px-2">
            <p className="text-sm text-gray-500">
              Showing {displayedData.length} of {filteredData.length} notices
            </p>
          </motion.div>
        )}

        {/* Table */}
        <motion.div
          variants={itemVariants}
          className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden border border-gray-200"
        >
          {/* Table Header */}
          <div className="hidden md:grid md:grid-cols-5 gap-4 bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 text-gray-600 p-5 font-medium border-b border-gray-200">
            <div className="text-sm">#</div>
            <div className="text-sm">College</div>
            <div className="text-sm">Title</div>
            <div className="text-sm">Date</div>
            <div className="text-sm">Description</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-100">
            {loading ? (
              // Loading skeletons
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : displayedData.length === 0 ? (
              <EmptyState />
            ) : (
              <AnimatePresence>
                {displayedData.map((item, index) => {
                  const collegeName = item.adminId?.collegeName || "Unknown College";
                  
                  return (
                    <motion.div
                      key={item._id}
                      custom={index}
                      variants={tableRowVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover="hover"
                      onClick={() => setSelectedNotice(item)}
                      className="grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-4 p-4 md:p-5 cursor-pointer transition-colors hover:bg-gray-50"
                    >
                      {/* Mobile Layout */}
                      <div className="md:hidden">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-700">{collegeName}</span>
                          </div>
                          <span className="text-xs text-gray-400">
                            #{(currentPage - 1) * entriesPerPage + index + 1}
                          </span>
                        </div>
                        <div className="mb-2">
                          <h3 className="text-base font-semibold text-gray-800">{item.title}</h3>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                          <HiOutlineCalendar className="w-3 h-3" />
                          <span>{formatDate(item.startDate)}</span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden md:block text-gray-500">
                        {(currentPage - 1) * entriesPerPage + index + 1}.
                      </div>
                      <div className="hidden md:flex items-center gap-1 text-gray-600">
                        <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium truncate">{collegeName}</span>
                      </div>
                      <div className="hidden md:block text-gray-700 font-medium truncate">
                        {item.title}
                      </div>
                      <div className="hidden md:block text-gray-600 text-sm">
                        {formatDate(item.startDate)}
                      </div>
                      <div className="hidden md:block text-gray-600 text-sm truncate">
                        {item.description}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </div>
        </motion.div>

        {/* Pagination */}
        {!loading && filteredData.length > 0 && totalPages > 1 && (
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6"
          >
            <div className="text-sm text-gray-500">
              Showing {(currentPage - 1) * entriesPerPage + 1} to{' '}
              {Math.min(currentPage * entriesPerPage, filteredData.length)} of{' '}
              {filteredData.length} notices
            </div>

            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === 1
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <HiOutlineChevronLeft className="w-5 h-5" />
              </motion.button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <motion.button
                      key={pageNum}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === totalPages
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <HiOutlineChevronRight className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Notice Details Modal */}
      <AnimatePresence>
        {selectedNotice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm"
            onClick={() => setSelectedNotice(null)}
          >
            <motion.div
              ref={modalRef}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 md:p-8">
                {/* Header */}
                <div className="flex items-start justify-between mb-6 sticky top-0 bg-white pb-4 border-b border-gray-200">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
                      {selectedNotice.title}
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <HiOutlineCalendar className="w-4 h-4" />
                      <span>{formatDate(selectedNotice.startDate)}</span>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setSelectedNotice(null)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <HiOutlineX className="w-5 h-5 text-gray-500" />
                  </motion.button>
                </div>

                {/* College Info */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <HiOutlineOfficeBuilding className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Posted by</p>
                      <p className="text-base md:text-lg font-semibold text-gray-800">
                        {selectedNotice.adminId?.collegeName || "Unknown College"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="prose max-w-none">
                  <h3 className="text-base md:text-lg font-semibold text-gray-700 mb-3">
                    Description
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 whitespace-pre-line leading-relaxed">
                    {selectedNotice.description}
                  </p>
                </div>

                {/* Close button at bottom for mobile */}
                <div className="mt-6 text-center md:hidden">
                  <button
                    onClick={() => setSelectedNotice(null)}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Notice;